import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';

export type ThemePreference = 'system' | 'light' | 'dark';

const STORAGE_KEY = 'theme-preference';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private mql = window.matchMedia?.('(prefers-color-scheme: dark)');

  private systemListener = (e: MediaQueryListEvent) => {
    // only follow system when preference is system
    if (this.currentPref === 'system') {
      this.applyTheme(e.matches ? 'dark' : 'light');
    }
  };

  private currentPref: ThemePreference = 'system';

  async init() {
    this.currentPref = await this.getPreference();
    await this.applyPreference(this.currentPref);
  }

  async setPreference(pref: ThemePreference) {
    this.currentPref = pref;
    await this.savePreference(pref);
    await this.applyPreference(pref);
  }

  async getPreference(): Promise<ThemePreference> {
    if (Capacitor.getPlatform() !== 'web') {
      const { value } = await Preferences.get({ key: STORAGE_KEY });
      return (value as ThemePreference) ?? 'system';
    }

    const v = localStorage.getItem(STORAGE_KEY) as ThemePreference | null;
    return v ?? 'system';
  }

  private async savePreference(pref: ThemePreference) {
    if (Capacitor.getPlatform() !== 'web') {
      await Preferences.set({ key: STORAGE_KEY, value: pref });
    } else {
      localStorage.setItem(STORAGE_KEY, pref);
    }
  }

  private async applyPreference(pref: ThemePreference) {
    if (pref === 'system') {
      this.applyTheme(this.isSystemDark() ? 'dark' : 'light');
      this.addSystemListener();
    } else {
      this.applyTheme(pref);
      this.removeSystemListener();
    }
  }

  private isSystemDark(): boolean {
    return !!this.mql?.matches;
  }

  private applyTheme(mode: 'light' | 'dark') {
    document.documentElement.classList.toggle('dark', mode === 'dark');
    // Helps native form controls + scrollbars in some environments
    document.documentElement.style.colorScheme = mode;
  }

  private addSystemListener() {
    if (!this.mql) return;
    if ('addEventListener' in this.mql) {
      this.mql.addEventListener('change', this.systemListener);
    } else {
      // @ts-ignore older Safari
      this.mql.addListener(this.systemListener);
    }
  }

  private removeSystemListener() {
    if (!this.mql) return;
    if ('removeEventListener' in this.mql) {
      this.mql.removeEventListener('change', this.systemListener);
    } else {
      // @ts-ignore older Safari
      this.mql.removeListener(this.systemListener);
    }
  }
}